(() => {
  if (window.nokoCompactVariantsLoaded) return;
  window.nokoCompactVariantsLoaded = true;
  const instances = new WeakMap();
  const normalize = value => value.trim().toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const kinds = {
    size: ['size', 'taglia', 'große', 'grosse', 'groesse'],
    color: ['color', 'colour', 'colore', 'farbe'],
    handle: ['handlebar', 'manubrio', 'lenker']
  };

  class CompactVariants {
    constructor(root) {
      this.root = root;
      this.guides = new Map();
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onProductRerender = this.onProductRerender.bind(this);
      this.root.addEventListener('click', this.onClick);
      this.root.addEventListener('change', this.onChange);
      this.observer = new MutationObserver(() => this.schedule());
      this.refresh();
      this.observe();
    }

    observe() {
      this.observer.observe(this.root, { childList: true, subtree: true });
    }

    schedule() {
      if (this.scheduled) return;
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        if (!this.root.isConnected) return;
        this.observer.disconnect();
        try { this.refresh(); } finally { this.observe(); }
      });
    }

    refresh() {
      const formId = this.root.dataset.nokoFormId;
      const picker = [...this.root.querySelectorAll('variant-picker')].find(element => element.getAttribute('form-id') === formId);
      const pickerBlock = picker?.closest('[data-block-type="variant-picker"]');
      const info = pickerBlock?.closest('.product-info');
      if (!pickerBlock || !info) return;
      const form = document.getElementById(formId);
      if (form !== this.form) {
        this.form?.removeEventListener('product:rerender', this.onProductRerender, true);
        this.form = form;
        this.form?.addEventListener('product:rerender', this.onProductRerender, true);
      }
      let box = pickerBlock.closest('.noko-variant-box') || info.querySelector('.noko-variant-box');
      if (!box) {
        // Backward-compatible fallback for a theme copy that has not received the SSR Liquid patch yet.
        box = document.createElement('div');
        box.className = 'noko-variant-box';
        pickerBlock.before(box);
        box.append(pickerBlock);
        const inventory = info.querySelector('[data-block-type="inventory"]');
        if (inventory) box.append(inventory);
      }
      picker.classList.add('noko-variant-picker');
      this.registerGuide('size', info.querySelector('[data-sg-open]'), info.querySelector('[data-sg-modal]'));
      this.registerGuide('handle', info.querySelector('.noko-hg__trigger'), info.querySelector('#noko-hg-overlay'));
      [...picker.querySelectorAll('fieldset')].forEach((field, index) => this.enhanceField(field, index));
      info.classList.add('noko-variants-ready');
    }

    onProductRerender(event) {
      if (event.detail?.productChange) return;
      const picker = [...this.root.querySelectorAll('variant-picker')].find(element => element.getAttribute('form-id') === this.root.dataset.nokoFormId);
      const block = picker?.closest('[data-block-type="variant-picker"]');
      const rerender = picker?.closest('product-rerender');
      if (!block?.hasAttribute('data-block-allow-rerender') || !rerender?.hasAttribute('allow-partial-rerender')) return;
      const incomingRoot = event.detail?.htmlFragment?.querySelector(`#${CSS.escape(rerender.id)}`);
      const incoming = [...(incomingRoot?.querySelectorAll('variant-picker') || [])].find(element => element.getAttribute('form-id') === this.root.dataset.nokoFormId);
      if (!incoming || incoming.getAttribute('handle') !== picker.getAttribute('handle')) return;
      for (const [kind, guide] of this.guides) {
        const guideBlock = guide.trigger.closest('[data-block-type="liquid"]');
        if (!guideBlock || !rerender.contains(guideBlock)) continue;
        const nextGuideBlock = incomingRoot.querySelector(`[data-block-id="${CSS.escape(guideBlock.dataset.blockId)}"]`);
        const triggerSelector = kind === 'size' ? '[data-sg-open]' : '.noko-hg__trigger';
        if (nextGuideBlock?.querySelector(triggerSelector)) this.preserveDuringRerender(guideBlock);
      }
      const fields = [...picker.querySelectorAll('fieldset')];
      const nextFields = [...incoming.querySelectorAll('fieldset')];
      if (fields.length !== nextFields.length) return;
      const pairs = [];
      const clean = value => value.replace(/\s+/g, ' ').trim();
      for (let index = 0; index < fields.length; index++) {
        const inputs = [...fields[index].querySelectorAll('input[data-option-position]')];
        const nextInputs = [...nextFields[index].querySelectorAll('input[data-option-position]')];
        if (!inputs.length || inputs.length !== nextInputs.length) return;
        for (let option = 0; option < inputs.length; option++) {
          const input = inputs[option];
          const nextInput = nextInputs[option];
          const label = input.nextElementSibling;
          const nextLabel = nextInput.nextElementSibling;
          if (input.id !== nextInput.id || input.name !== nextInput.name || input.value !== nextInput.value) return;
          if (!label?.matches('label.block-swatch') || !nextLabel?.matches('label.block-swatch')) return;
          if (clean(label.dataset.nokoValueLabel || '') !== clean(nextLabel.textContent)) return;
          const chip = label.querySelector('.block-swatch__color');
          const nextChip = nextLabel.querySelector('.block-swatch__color');
          if (Boolean(chip) !== Boolean(nextChip)) return;
          pairs.push({ input, nextInput, label, nextLabel, chip, nextChip });
        }
      }
      const syncAttributes = (current, next, preserve = () => false) => {
        [...current.attributes].forEach(attribute => {
          if (!preserve(attribute.name) && !next.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
        });
        [...next.attributes].forEach(attribute => {
          if (!preserve(attribute.name) && current.getAttribute(attribute.name) !== attribute.value) current.setAttribute(attribute.name, attribute.value);
        });
      };
      for (const { input, nextInput, label, nextLabel, chip, nextChip } of pairs) {
        syncAttributes(input, nextInput);
        input.checked = nextInput.checked;
        input.disabled = nextInput.disabled;
        syncAttributes(label, nextLabel, name => name.startsWith('data-noko-') || name === 'aria-label');
        if (chip) syncAttributes(chip, nextChip);
      }
      fields.forEach((field, index) => {
        const selected = field.querySelector('.noko-option-selected');
        const nextSelected = nextFields[index].querySelector('.variant-picker__option-info > .h-stack > span');
        if (selected && nextSelected && selected.textContent !== nextSelected.textContent) selected.textContent = nextSelected.textContent;
      });
      const variant = picker.querySelector('script[data-variant]');
      const nextVariant = incoming.querySelector('script[data-variant]');
      if (variant && nextVariant && variant.textContent !== nextVariant.textContent) variant.textContent = nextVariant.textContent;
      this.preserveDuringRerender(block);
    }

    preserveDuringRerender(block) {
      if (!block.hasAttribute('data-block-allow-rerender')) return;
      const value = block.getAttribute('data-block-allow-rerender');
      block.removeAttribute('data-block-allow-rerender');
      queueMicrotask(() => {
        if (block.isConnected) block.setAttribute('data-block-allow-rerender', value);
      });
    }

    registerGuide(kind, trigger, modal) {
      if (!trigger || !modal || this.guides.get(kind)?.trigger === trigger) return;
      this.guides.get(kind)?.observer.disconnect();
      if (!modal.id) modal.id = `${this.root.dataset.nokoFormId}-${kind}-guide`;
      const guide = { trigger, modal, open: modal.classList.contains('is-open'), returnId: null };
      guide.observer = new MutationObserver(() => {
        const open = modal.classList.contains('is-open');
        this.root.querySelectorAll(`[data-noko-guide="${kind}"]`).forEach(button => button.setAttribute('aria-expanded', String(open)));
        if (guide.open && !open && guide.returnId) {
          const target = document.getElementById(guide.returnId);
          if (target && this.root.contains(target)) target.focus({ preventScroll: true });
        }
        guide.open = open;
      });
      guide.observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
      this.guides.set(kind, guide);
    }

    enhanceField(field, index) {
      const legend = field.querySelector('legend');
      const header = field.querySelector('.variant-picker__option-info');
      const values = field.querySelector('.variant-picker__option-values');
      if (!legend || !header || !values) return;
      const title = normalize(legend.textContent.replace(/:\s*$/, ''));
      const kind = field.dataset.nokoKind || Object.keys(kinds).find(key => kinds[key].includes(title)) || 'other';
      field.dataset.nokoKind = kind;
      const labels = [...values.children].filter(element => element.matches('label.block-swatch, a.block-swatch'));
      if (!labels.length) return;
      field.style.setProperty('--noko-option-columns', String(Math.min(kind === 'handle' || kind === 'other' ? 2 : 4, labels.length)));
      if (!field.dataset.nokoEnhanced) {
        const number = document.createElement('span');
        number.className = 'noko-option-number';
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(index + 1).padStart(2, '0');
        legend.prepend(number);
        const textNode = [...legend.childNodes].find(node => node.nodeType === Node.TEXT_NODE);
        if (textNode) textNode.textContent = textNode.textContent.replace(/:\s*$/, '');
        const selected = header.querySelector(':scope > .h-stack > span');
        if (selected) selected.classList.add('noko-option-selected');
        field.dataset.nokoEnhanced = 'true';
      }
      for (const label of labels) this.enhanceLabel(label, kind);
      const guide = this.guides.get(kind);
      if (guide && !header.querySelector('[data-noko-guide]')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.id = `${this.root.dataset.nokoFormId}-${kind}-proxy`;
        button.className = 'noko-option-guide';
        button.dataset.nokoGuide = kind;
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', guide.modal.id);
        button.setAttribute('aria-expanded', String(guide.open));
        button.textContent = kind === 'size' ? this.root.dataset.nokoSizeLabel : this.root.dataset.nokoHandleLabel;
        header.append(button);
      }
      const guideButton = header.querySelector('[data-noko-guide]');
      if (guide && guideButton) {
        guideButton.setAttribute('aria-controls', guide.modal.id);
        guideButton.setAttribute('aria-expanded', String(guide.open));
        guide.trigger.dataset.nokoProxyActive = 'true';
        guide.trigger.closest('[data-block-type="liquid"]')?.classList.add('noko-guide-source');
      }
    }

    enhanceLabel(label, kind) {
      if (label.dataset.nokoEnhanced) return;
      const span = [...label.children].find(element => element.tagName === 'SPAN' && !element.classList.contains('block-swatch__color'));
      if (!span) return;
      const full = span.textContent.trim();
      let primary = full;
      let secondary = '';
      if (kind === 'color' && full.includes(' ')) {
        const splitAt = full.indexOf(' ');
        primary = full.slice(0, splitAt);
        secondary = full.slice(splitAt + 1);
      } else if (kind === 'handle') {
        const splitAt = full.indexOf(' - ');
        if (splitAt > -1) {
          primary = full.slice(0, splitAt);
          secondary = full.slice(splitAt + 3);
        } else if (/\s(aluminium|aluminum|alluminio)$/i.test(full)) {
          const splitAt = full.lastIndexOf(' ');
          primary = full.slice(0, splitAt);
          secondary = full.slice(splitAt + 1);
        }
      }
      span.className = 'noko-option-copy';
      span.textContent = '';
      const name = document.createElement('span');
      name.className = 'noko-option-name';
      name.textContent = primary;
      span.append(name);
      if (secondary) {
        const detail = document.createElement('span');
        detail.className = 'noko-option-detail';
        detail.textContent = secondary;
        span.append(detail);
      }
      label.setAttribute('aria-label', full);
      label.dataset.nokoValueLabel = full;
      label.dataset.nokoEnhanced = 'true';
    }

    onClick(event) {
      const button = event.target.closest('[data-noko-guide]');
      if (!button || !this.root.contains(button)) return;
      const guide = this.guides.get(button.dataset.nokoGuide);
      if (!guide) return;
      guide.returnId = button.id;
      guide.trigger.click();
      if (guide.modal.classList.contains('is-open')) {
        guide.modal.querySelector('button, input, a[href]')?.focus({ preventScroll: true });
      }
    }

    onChange(event) {
      if (!event.target.matches('input[data-option-position]')) return;
      const field = event.target.closest('fieldset[data-noko-kind]');
      const label = event.target.nextElementSibling;
      const selected = field?.querySelector('.noko-option-selected');
      if (selected && label?.dataset.nokoValueLabel) selected.textContent = label.dataset.nokoValueLabel;
    }

    destroy() {
      this.observer.disconnect();
      this.form?.removeEventListener('product:rerender', this.onProductRerender, true);
      this.guides.forEach(guide => guide.observer.disconnect());
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('change', this.onChange);
      instances.delete(this.root);
    }
  }

  const mount = scope => {
    const roots = [...scope.querySelectorAll('[data-noko-compact-variants]')];
    if (scope.matches?.('[data-noko-compact-variants]')) roots.unshift(scope);
    roots.forEach(root => {
      if (!instances.has(root)) instances.set(root, new CompactVariants(root));
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-noko-compact-variants]').forEach(root => instances.get(root)?.destroy());
  });
})();
